import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SettingsIcon from "@mui/icons-material/Settings";
import VisibilityIcon from "@mui/icons-material/Visibility";
import React from "react";
import { WorkspaceVisibility } from "../../../../types/workspace";
import { WorkspacesTableRowDefinition } from "../../types";

interface ActionMenuItem {
  label?: string;
  icon?: React.JSX.Element;
  renderLabel?: (row: WorkspacesTableRowDefinition) => string;
  renderIcon?: (row: WorkspacesTableRowDefinition) => React.ReactNode;
  onClick: (row: WorkspacesTableRowDefinition) => void;
  dividerBefore?: boolean;
  testId?: string;
}

export enum ActionOption {
  View = "view",
  EditDetails = "edit-details",
  EditConfig = "edit-config",
  Duplicate = "duplicate",
  SetPrivate = "set-private",
  SetPublic = "set-public",
  Delete = "delete",
}

interface CreateMenuActionOptions {
  handleActionClick: (
    action: ActionOption,
    row: WorkspacesTableRowDefinition
  ) => void;
  row: WorkspacesTableRowDefinition;
}

export const createMenuActions = ({
  handleActionClick,
  row,
}: CreateMenuActionOptions): ActionMenuItem[] => {
  const { permissions } = row;
  const canWrite = permissions?.can_write ?? false;
  const canDuplicate = permissions?.can_duplicate ?? false;

  const actions: ActionMenuItem[] = [
    {
      label: "View",
      icon: <VisibilityIcon fontSize="small" />,
      onClick: (row) => handleActionClick(ActionOption.View, row),
    },
  ];

  if (canWrite) {
    actions.push({
      label: "Edit Project Details",
      icon: <EditIcon fontSize="small" />,
      onClick: (row) => handleActionClick(ActionOption.EditDetails, row),
      dividerBefore: true,
      testId: "workspace-edit-btn",
    });
    actions.push({
      label: "Edit Configuration",
      icon: <SettingsIcon fontSize="small" />,
      onClick: (row) => handleActionClick(ActionOption.EditConfig, row),
    });
  }

  if (canDuplicate) {
    actions.push({
      label: "Duplicate",
      icon: <FileCopyIcon fontSize="small" />,
      onClick: (row) => handleActionClick(ActionOption.Duplicate, row),
      dividerBefore: !canWrite,
      testId: "workspace-duplicate-btn",
    });
  }

  if (canWrite) {
    actions.push({
      renderLabel: ({ visibilityStatus }) =>
        visibilityStatus === WorkspaceVisibility.PRIVATE
          ? "Set to Public"
          : "Set to Private",
      renderIcon: ({ visibilityStatus }) =>
        visibilityStatus === WorkspaceVisibility.PRIVATE ? (
          <LockOpenOutlinedIcon fontSize="small" />
        ) : (
          <LockOutlinedIcon fontSize="small" />
        ),
      onClick: (row) => {
        if (row.visibilityStatus === WorkspaceVisibility.PRIVATE) {
          handleActionClick(ActionOption.SetPublic, row);
        } else {
          handleActionClick(ActionOption.SetPrivate, row);
        }
      },
      dividerBefore: true,
      testId: "workspace-visibility-toggle-btn",
    });
    actions.push({
      label: "Delete",
      icon: (
        // This is a workaround to make the E2E test pass. MUI V7 does not support data-testid on SVG icons anymore
        <div data-testid="DeleteOutlineIcon">
          <DeleteOutlineIcon fontSize="small" />
        </div>
      ),
      onClick: (row) => handleActionClick(ActionOption.Delete, row),
      dividerBefore: true,
      testId: "workspace-delete-btn",
    });
  }

  return actions;
};
